/**
 * VISTOERA_TENANT_TEXT_ASSET_CENTER_CANONICAL_UNIFIED_LIBRARY_P0_V1 —
 * "文本资产". A pure presentation layer over the unified READ MODEL served
 * by `app/api/routes/saas_text_assets.py` (see `../api`'s own docstring
 * for the full canonical source map). This component never fabricates
 * content, tags, or usage counts — every field rendered here traces back
 * to a real value the backend returned, or an explicit "—" placeholder.
 *
 * Same "shell" conventions as `ImageAssetCenterPage`/`VideoAssetsPage`:
 * backend-paginated/filtered/sorted list, lazy-loaded Detail Drawer (full
 * content is only fetched on open, never in the list payload), workspace-
 * scoped via `useWorkspaceState()`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  Drawer,
  Dropdown,
  Empty,
  Input,
  Modal,
  Select,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from 'antd'
import type { MenuProps, TableProps } from 'antd'
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  FileTextOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  AppstoreAddOutlined,
} from '@ant-design/icons'
import {
  createContentProject,
  createTextAsset,
  deleteTextAsset,
  getTextAsset,
  listTextAssets,
  updateTextAsset,
  updateTextAssetVisibility,
  type TextAssetDetail,
  type TextAssetItem,
  type TextAssetSort,
  type TextAssetTeamBlock,
  type TextAssetType,
  type TextLibraryView,
} from '../api'
import { syncCanvasNavigationContextFromProject } from '../navigation/canvasNavigationContext'
import { useWorkspaceState } from './WorkspaceContext'
import { openCanvasFromWorkspace } from './canvasWorkspaceTransition'
import {
  makeJoinTextAssetSeedNonce,
  writeJoinTextAssetSeed,
} from './textAssetJoinCanvasSeed'
import { VistoeraActionConfirm } from '../ui/action'
import {
  buildCreatorSelectOptions,
  creatorFilterAfterViewChange,
  sanitizeCreatorFilter,
} from './team/creatorFilterOptions'
import { textAssetPermissions } from './team/textAssetPermissions'
import './text-asset-center.css'

const PAGE_SIZE_OPTIONS = [20, 50, 100]

/** Tab order is product-mandated (VistoeraTASK-00553 §2): 全部, 生成文本,
 * 普通文本, 创意, 剧本, 分镜, 文案, 字幕/转录 — GENERATED_TEXT must sort
 * immediately after "all". Mirrors backend `ALL_TYPES` order exactly. */
const TYPE_TABS: Array<TextAssetType | 'all'> = [
  'all',
  'GENERATED_TEXT',
  'TEXT',
  'CREATIVE',
  'SCRIPT',
  'STORYBOARD',
  'COPY',
  'TRANSCRIPT',
]

const TYPE_TAG_COLOR: Record<TextAssetType, string> = {
  GENERATED_TEXT: 'geekblue',
  TEXT: 'default',
  CREATIVE: 'purple',
  SCRIPT: 'blue',
  STORYBOARD: 'orange',
  COPY: 'green',
  TRANSCRIPT: 'cyan',
}

/** TYPE TEXT library notes only (CanvasNode:text_asset / WorkspaceTextAsset).
 * Creative/Script/Storyboard/Transcript follow their own edit contracts. */
function canManageText(item: TextAssetItem, team: TextAssetTeamBlock | null): boolean {
  return textAssetPermissions(item, team).canEdit
}

function formatAbsoluteTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${mo}-${day} ${hh}:${mm}`
}

/** Relations-tab "关联节点" label — derived only from the real
 * `canonical_source_type` the backend returned, never guessed from a
 * title (spec §63 "不要通过标题相似猜 lineage"). */
function canonicalNodeLabel(canonicalSourceType: string, t: (k: string) => string): string {
  const map: Record<string, string> = {
    'CanvasNode:generate_text': t('nodeKind.generatedText'),
    'CanvasNode:text_asset': t('nodeKind.text'),
    'CanvasNode:generate_creative': t('nodeKind.creative'),
    'CanvasNode:script_generation': t('nodeKind.script'),
    'CanvasNode:storyboard_generation': t('nodeKind.storyboard'),
    'ReferenceContent:transcript': t('nodeKind.transcript'),
    'VideoDirectorSession:selected_proposal': t('nodeKind.videoDirectorCreative'),
    'VideoDirectorSession:confirmed_storyboard': t('nodeKind.videoDirectorStoryboard'),
  }
  return map[canonicalSourceType] || canonicalSourceType
}

/** Mirrors the REAL `ScriptDialogueLine`/`ScriptScene` shape from
 * `saas/api.ts` (the canonical `ScriptArtifact` produced by
 * `script_generation` nodes) — NOT a placeholder guess. */
interface ScriptDialogueLine {
  character: string
  line: string
}

interface ScriptScene {
  scene_number?: number
  setting?: string
  action?: string
  voiceover?: string | null
  dialogue?: ScriptDialogueLine[]
  [key: string]: unknown
}

/** Mirrors the REAL `ShotDialogueLine`/`ShotArtifact` shape from
 * `saas/api.ts` (the canonical `StoryboardArtifact` produced by
 * `storyboard_generation` nodes). `scene` can be a plain string OR
 * `{ scene_id, name, asset_id }` — never assume a plain string. */
interface ShotDialogueLine {
  character_id: string
  text: string
}

interface StoryboardShot {
  shot_index?: number
  title?: string
  description?: string
  camera_description?: string | null
  scene?: string | { scene_id?: string | null; name: string; asset_id?: string | null }
  duration_sec?: number
  dialogue?: ShotDialogueLine[]
  [key: string]: unknown
}

/** `ShotArtifact.scene` is `string | { name, ... }` — read the real
 * shape rather than assuming it's always a plain string (spec §26). */
function shotSceneLabel(scene: StoryboardShot['scene']): string {
  if (typeof scene === 'string') return scene
  if (scene && typeof scene === 'object' && typeof scene.name === 'string') return scene.name
  return ''
}

/** Mirrors the REAL `VIDEO_DIRECTOR_PROPOSAL` skill output schema
 * (backend/app/ai_harness/skills/video_director_proposal/v1/output.schema.json)
 * — a SECOND real Creative canonical source (Video Director / Classic
 * Video Project wizard), distinct from the Canvas `generate_creative`
 * shape above. Detected by the presence of `one_line_concept`, which
 * only exists on this schema. */
interface VideoDirectorProposalShot {
  shot_index?: number
  duration_seconds?: number
  shot_type?: string
  framing?: string
  camera_movement?: string
  subject_action?: string
  environment_action?: string
  visual_description?: string
  transition?: string
  audio_note?: string
  [key: string]: unknown
}

function isVideoDirectorProposal(result: Record<string, unknown>): boolean {
  return typeof result.one_line_concept === 'string'
}

/** Mirrors the REAL `VIDEO_SCRIPT_STORYBOARD` skill output schema
 * (backend/app/ai_harness/skills/video_script_storyboard/v1/output.schema.json)
 * — a SECOND real Storyboard canonical source (Video Director / Classic
 * Video Project wizard). Detected by the presence of `narrative_script`,
 * which only exists on this schema. */
interface VideoDirectorStoryboardShot {
  shot_id?: string
  shot_number?: number
  duration_seconds?: number
  purpose?: string
  visual_description?: string
  subject?: string
  subject_action?: string
  environment?: string
  [key: string]: unknown
}

function isVideoDirectorStoryboard(result: Record<string, unknown>): boolean {
  return typeof result.narrative_script === 'string'
}

interface TranscriptSegmentRow {
  segment_index: number
  start_ms: number
  end_ms: number
  text: string
  speaker: string | null
}

function msToClock(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/** Builds a plain-text, readable representation for "复制"/clipboard —
 * never dumps raw structured JSON at the user (spec §59/§68). */
function buildReadableText(detail: TextAssetDetail, t: (k: string, o?: Record<string, unknown>) => string): string {
  const d = (detail.detail || {}) as Record<string, unknown>
  if (detail.type === 'TEXT' || detail.type === 'GENERATED_TEXT') {
    return typeof d.full_text === 'string' ? d.full_text : detail.preview_text
  }
  if (detail.type === 'CREATIVE') {
    const result = (d.result || {}) as Record<string, unknown>
    const lines: string[] = [detail.title]
    const fieldMap: Array<[string, string]> = isVideoDirectorProposal(result)
      ? [
          ['one_line_concept', t('detail.content.creativeOneLineConcept')],
          ['creative_direction', t('detail.content.creativeDirection')],
          ['narrative_structure', t('detail.content.creativeNarrativeStructure')],
          ['opening_hook', t('detail.content.creativeOpeningHook')],
          ['ending_image', t('detail.content.creativeEndingImage')],
          ['why_it_fits', t('detail.content.creativeWhyItFits')],
        ]
      : [
          ['premise', t('detail.content.creativePremise')],
          ['hook', t('detail.content.creativeHook')],
          ['storyDirection', t('detail.content.creativeStoryDirection')],
          ['rationale', t('detail.content.creativeRationale')],
        ]
    for (const [key, label] of fieldMap) {
      const v = result[key]
      if (typeof v === 'string' && v.trim()) lines.push(`\n${label}\n${v.trim()}`)
    }
    return lines.join('\n')
  }
  if (detail.type === 'SCRIPT') {
    const result = (d.result || {}) as Record<string, unknown>
    const scenes = Array.isArray(result.scenes) ? (result.scenes as ScriptScene[]) : []
    const lines: string[] = [detail.title]
    if (typeof result.logline === 'string' && result.logline.trim()) lines.push(`\n${result.logline.trim()}`)
    if (typeof result.synopsis === 'string' && result.synopsis.trim()) lines.push(`\n${result.synopsis.trim()}`)
    scenes.forEach((scene, idx) => {
      lines.push(`\n${t('detail.content.scriptSceneFallback', { index: scene.scene_number ?? idx + 1 })}`)
      if (typeof scene.setting === 'string' && scene.setting.trim()) lines.push(scene.setting.trim())
      if (typeof scene.action === 'string' && scene.action.trim()) lines.push(scene.action.trim())
      if (typeof scene.voiceover === 'string' && scene.voiceover.trim()) lines.push(scene.voiceover.trim())
      const dialogue = Array.isArray(scene.dialogue) ? scene.dialogue : []
      dialogue.forEach((line) => {
        if (line && typeof line.line === 'string' && line.line.trim()) {
          lines.push(line.character ? `${line.character}：${line.line.trim()}` : line.line.trim())
        }
      })
    })
    return lines.join('\n')
  }
  if (detail.type === 'STORYBOARD') {
    const result = (d.result || {}) as Record<string, unknown>
    const lines: string[] = [detail.title]
    if (isVideoDirectorStoryboard(result)) {
      const shots = Array.isArray(result.shots) ? (result.shots as VideoDirectorStoryboardShot[]) : []
      if (typeof result.creative_summary === 'string' && result.creative_summary.trim()) lines.push(`\n${result.creative_summary.trim()}`)
      if (typeof result.narrative_script === 'string' && result.narrative_script.trim()) lines.push(`\n${result.narrative_script.trim()}`)
      shots.forEach((shot, idx) => {
        lines.push(`\n${t('detail.content.storyboardShot', { index: shot.shot_number ?? idx + 1 })}`)
        if (typeof shot.visual_description === 'string' && shot.visual_description.trim()) lines.push(shot.visual_description.trim())
        if (typeof shot.subject_action === 'string' && shot.subject_action.trim()) lines.push(shot.subject_action.trim())
      })
      return lines.join('\n')
    }
    const shots = Array.isArray(result.shots) ? (result.shots as StoryboardShot[]) : []
    if (typeof result.synopsis === 'string' && result.synopsis.trim()) lines.push(`\n${result.synopsis.trim()}`)
    shots.forEach((shot, idx) => {
      lines.push(`\n${t('detail.content.storyboardShot', { index: (shot.shot_index ?? idx) + 1 })}`)
      const sceneLabel = shotSceneLabel(shot.scene)
      if (sceneLabel) lines.push(sceneLabel)
      if (typeof shot.title === 'string' && shot.title.trim()) lines.push(shot.title.trim())
      if (typeof shot.description === 'string' && shot.description.trim()) lines.push(shot.description.trim())
      if (typeof shot.camera_description === 'string' && shot.camera_description.trim()) lines.push(shot.camera_description.trim())
      const dialogue = Array.isArray(shot.dialogue) ? shot.dialogue : []
      dialogue.forEach((line) => {
        if (line && typeof line.text === 'string' && line.text.trim()) lines.push(line.text.trim())
      })
    })
    return lines.join('\n')
  }
  if (detail.type === 'TRANSCRIPT') {
    const segments = Array.isArray(d.segments) ? (d.segments as TranscriptSegmentRow[]) : []
    return segments
      .map((seg) => `[${msToClock(seg.start_ms)}] ${seg.speaker || t('detail.content.transcriptSpeakerFallback')}: ${seg.text}`)
      .join('\n')
  }
  return detail.preview_text
}

export default function TextAssetsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('textAssets')
  const { t: tActions } = useTranslation('actions')
  const { t: tImageTeam } = useTranslation('imageAssets')
  const { t: tProjects } = useTranslation('contentProjects')
  const { workspace } = useWorkspaceState()
  const workspaceId = workspace?.id || ''

  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<TextAssetItem[]>([])
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState<Record<string, number>>({})
  const [availableSources, setAvailableSources] = useState<Array<{ value: string; count: number }>>([])
  const [loadError, setLoadError] = useState<string | null>(null)

  const [team, setTeam] = useState<TextAssetTeamBlock | null>(null)
  const teamEnabled = Boolean(team?.enabled)
  const teamEnabledRef = useRef(false)
  const [libraryView, setLibraryView] = useState<TextLibraryView>('mine')
  const [creatorFilter, setCreatorFilter] = useState<string>('all')

  const [q, setQ] = useState('')
  const [typeTab, setTypeTab] = useState<TextAssetType | 'all'>('all')
  const [sourceFilter, setSourceFilter] = useState<string>('all')
  const [sort, setSort] = useState<TextAssetSort>('updated_desc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const [createOpen, setCreateOpen] = useState(false)
  const [createTitle, setCreateTitle] = useState('')
  const [createBody, setCreateBody] = useState('')
  const [creating, setCreating] = useState(false)

  const [drawerId, setDrawerId] = useState<string | null>(null)
  const [drawerDetail, setDrawerDetail] = useState<TextAssetDetail | null>(null)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [drawerError, setDrawerError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [editBody, setEditBody] = useState('')
  const [editTitle, setEditTitle] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    if (!workspaceId) return
    setLoading(true)
    setLoadError(null)
    try {
      const { data } = await listTextAssets(workspaceId, {
        q: q.trim() || undefined,
        type: typeTab,
        source: sourceFilter === 'all' ? undefined : sourceFilter,
        sort,
        page,
        page_size: pageSize,
        ...(teamEnabledRef.current
          ? {
              view: libraryView,
              ...(creatorFilter !== 'all' ? { creator_id: creatorFilter } : {}),
            }
          : {}),
      })
      teamEnabledRef.current = Boolean(data.team?.enabled)
      setTeam(data.team ?? null)
      setItems(data.items)
      setTotal(data.total)
      setSummary(data.summary)
      setAvailableSources(data.available_sources)
      const nextCreator = sanitizeCreatorFilter(creatorFilter, data.team?.creator_options)
      if (nextCreator !== creatorFilter) setCreatorFilter(nextCreator)
    } catch (e) {
      setLoadError((e as Error).message || t('toast.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [workspaceId, q, typeTab, sourceFilter, sort, page, pageSize, libraryView, creatorFilter, t])

  const onChangeLibraryView = (next: TextLibraryView) => {
    if (next === libraryView) return
    setLibraryView(next)
    setCreatorFilter((prev) => creatorFilterAfterViewChange(next, prev))
    setPage(1)
  }

  const creatorLabel = useCallback(
    (item: TextAssetItem) => {
      if (!item.creator) return t('table.creatorUnrecorded')
      return item.creator.is_me ? tImageTeam('team.creator.me') : item.creator.display_name
    },
    [t, tImageTeam],
  )

  useEffect(() => {
    void load()
  }, [load])

  const openDrawer = useCallback(
    async (presentationId: string) => {
      setDrawerId(presentationId)
      setEditing(false)
      setDrawerDetail(null)
      setDrawerError(null)
      setDrawerLoading(true)
      try {
        const { data } = await getTextAsset(workspaceId, presentationId)
        setDrawerDetail(data)
      } catch (e) {
        setDrawerError((e as Error).message || t('toast.detailLoadFailed'))
      } finally {
        setDrawerLoading(false)
      }
    },
    [workspaceId, t],
  )

  const closeDrawer = () => {
    setDrawerId(null)
    setDrawerDetail(null)
    setDrawerError(null)
    setEditing(false)
  }

  const refreshDrawerAndList = async (presentationId: string) => {
    await load()
    try {
      const { data } = await getTextAsset(workspaceId, presentationId)
      setDrawerDetail(data)
    } catch {
      /* list already refreshed; drawer keeps last-known-good detail */
    }
  }

  const onCreate = async () => {
    if (!createBody.trim()) {
      message.error(t('create.bodyRequired'))
      return
    }
    setCreating(true)
    try {
      await createTextAsset(workspaceId, { title: createTitle.trim() || undefined, body: createBody })
      message.success(t('toast.created'))
      setCreateOpen(false)
      setCreateTitle('')
      setCreateBody('')
      setPage(1)
      void load()
    } catch (e) {
      message.error((e as Error).message || t('toast.createFailed'))
    } finally {
      setCreating(false)
    }
  }

  const startEdit = () => {
    if (!drawerDetail) return
    setEditTitle(drawerDetail.title)
    setEditBody(typeof drawerDetail.detail.full_text === 'string' ? (drawerDetail.detail.full_text as string) : '')
    setEditing(true)
  }

  const onSaveEdit = async () => {
    if (!drawerId) return
    if (!editBody.trim()) {
      message.error(t('create.bodyRequired'))
      return
    }
    setSaving(true)
    try {
      await updateTextAsset(workspaceId, drawerId, { title: editTitle.trim(), body: editBody })
      message.success(t('toast.updated'))
      setEditing(false)
      await refreshDrawerAndList(drawerId)
    } catch (e) {
      message.error((e as Error).message || t('toast.updateFailed'))
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async (presentationId: string) => {
    try {
      await deleteTextAsset(workspaceId, presentationId)
      message.success(t('toast.deleted'))
      if (drawerId === presentationId) closeDrawer()
      // Preserve filters; if this was the last row on a page > 1, step back.
      if (items.length <= 1 && page > 1) {
        setPage((p) => Math.max(1, p - 1))
      } else {
        void load()
      }
    } catch (e) {
      message.error((e as Error).message || t('toast.deleteFailed'))
    }
  }

  /** Mirror ImageAssetCenterPage `confirmDeleteSingle` (Modal.confirm +
   * danger OK). Calls text delete API only — never image delete. */
  const confirmDeleteText = (item: TextAssetItem) => {
    Modal.confirm({
      title: t('delete.confirmTitle', { name: item.title || '—' }),
      content: t('delete.confirmDescription', { name: item.title || '—' }),
      okText: tActions('delete'),
      okType: 'danger',
      cancelText: t('actions.cancel'),
      centered: true,
      onOk: () => onDelete(item.id),
    })
  }

  const changeTextVisibility = async (item: TextAssetItem, visibility: 'workspace' | 'private') => {
    try {
      await updateTextAssetVisibility(workspaceId, item.id, visibility)
      message.success(
        visibility === 'private' ? t('toast.madePrivate') : t('toast.madeShared'),
      )
      // Keep current view / filters / page; list refresh will drop items that
      // no longer match the active team tab (e.g. shared → private).
      void load()
      if (drawerId === item.id) {
        await refreshDrawerAndList(item.id)
      }
    } catch (e) {
      message.error((e as Error).message || t('toast.visibilityFailed'))
    }
  }

  const textRowMenuItems = (item: TextAssetItem): MenuProps['items'] => {
    const perms = textAssetPermissions(item, team)
    const menu: NonNullable<MenuProps['items']> = []
    if (perms.canEdit) {
      menu.push({
        key: 'edit',
        label: tImageTeam('team.menu.edit'),
        onClick: () => {
          setPendingEditAfterOpen(true)
          void openDrawer(item.id)
        },
        'data-testid': 'tac-menu-edit',
      })
    }
    menu.push({
      key: 'join-canvas',
      label: tImageTeam('team.menu.joinCanvas'),
      onClick: () => void onJoinCanvas(item),
      'data-testid': 'tac-menu-join-canvas',
    })
    if (perms.showShareToggle && perms.canChangeVisibility) {
      menu.push(
        item.visibility === 'private'
          ? {
              key: 'share',
              label: tImageTeam('team.menu.makeShared'),
              onClick: () => void changeTextVisibility(item, 'workspace'),
              'data-testid': 'tac-menu-make-shared',
            }
          : {
              key: 'unshare',
              label: tImageTeam('team.menu.makePrivate'),
              onClick: () => void changeTextVisibility(item, 'private'),
              'data-testid': 'tac-menu-make-private',
            },
      )
    }
    menu.push({
      key: 'detail',
      label: tImageTeam('team.menu.detail'),
      onClick: () => void openDrawer(item.id),
      'data-testid': 'tac-menu-detail',
    })
    if (perms.canDelete) {
      menu.push(
        { type: 'divider' },
        {
          key: 'delete',
          danger: true,
          label: tImageTeam('team.menu.delete'),
          onClick: () => confirmDeleteText(item),
          'data-testid': 'tac-menu-delete',
        },
      )
    }
    return menu
  }

  const onCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      message.success(t('toast.copied'))
    } catch {
      message.error(t('toast.copyFailed'))
    }
  }

  const openSource = (item: Pick<TextAssetItem, 'type' | 'project_id' | 'canonical_source_id'>) => {
    if (item.type === 'TRANSCRIPT') {
      navigate(`/workspace/${workspaceId}/competitive-content-analysis/${item.canonical_source_id}`)
      return
    }
    if (item.project_id) {
      navigate(`/workspace/${workspaceId}/content-production/${item.project_id}/canvas`)
    }
  }

  const sourceOptions = useMemo(
    () => [
      { value: 'all', label: t('filters.allSources') },
      ...availableSources.map((s) => ({ value: s.value, label: `${t(`sources.${s.value}`, { defaultValue: s.value })} (${s.count})` })),
    ],
    [availableSources, t],
  )

  // Row-level "编辑" (spec §67 — only the canonical TEXT node is editable
  // here) opens the Drawer AND enters edit mode without a second network
  // round trip once the just-triggered `openDrawer()` fetch resolves.
  const [pendingEditAfterOpen, setPendingEditAfterOpen] = useState(false)
  useEffect(() => {
    if (pendingEditAfterOpen && drawerDetail && !drawerLoading) {
      setPendingEditAfterOpen(false)
      startEdit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEditAfterOpen, drawerDetail, drawerLoading])

  /** VistoeraTASK-00626 — create a NEW blank Content Project, seed one
   * text_asset node (content snapshot), open with the same Workspace→Canvas
   * Enter animation as Image Assets / V logo. Never mutates the library row. */
  const joinCanvasInFlightRef = useRef(false)
  const onJoinCanvas = useCallback(
    async (item: TextAssetItem) => {
      if (!workspaceId) return
      if (joinCanvasInFlightRef.current) return
      joinCanvasInFlightRef.current = true
      const hide = message.loading({ content: t('joinCanvas.opening'), duration: 0 })
      try {
        const { data: detail } = await getTextAsset(workspaceId, item.id)
        const body = buildReadableText(detail, t).trim()
        if (!body) {
          message.error(t('joinCanvas.emptyBody'))
          return
        }
        const { data: project } = await createContentProject(
          workspaceId,
          tProjects('unnamedProject'),
        )
        if (!project?.id) {
          message.error(t('joinCanvas.createFailed'))
          return
        }
        writeJoinTextAssetSeed({
          workspaceId,
          projectId: project.id,
          presentationId: item.id,
          title: detail.title || item.title || '',
          text: body,
          assetType: detail.type || item.type,
          libraryCanonicalSourceType: detail.canonical_source_type || item.canonical_source_type,
          libraryCanonicalSourceId: detail.canonical_source_id || item.canonical_source_id,
          nonce: makeJoinTextAssetSeedNonce(),
        })
        syncCanvasNavigationContextFromProject(workspaceId, project.id, 'content-production')
        openCanvasFromWorkspace(
          navigate,
          `/workspace/${workspaceId}/content-production/${project.id}/canvas`,
        )
      } catch (e) {
        message.error((e as Error)?.message || t('joinCanvas.openFailed'))
      } finally {
        hide()
        joinCanvasInFlightRef.current = false
      }
    },
    [workspaceId, navigate, t, tProjects],
  )

  const columns: TableProps<TextAssetItem>['columns'] = [
    {
      title: t('table.name'),
      dataIndex: 'title',
      key: 'title',
      ellipsis: false,
      render: (_: string, item) => (
        <button type="button" className="tac-name-cell" onClick={() => void openDrawer(item.id)}>
          <FileTextOutlined className="tac-name-icon" />
          <span className="tac-name-text">
            <span className="tac-name-title" title={item.title}>
              {item.title}
            </span>
            <span className="tac-name-preview">{item.preview_text || '—'}</span>
          </span>
        </button>
      ),
    },
    {
      title: t('table.type'),
      dataIndex: 'type',
      key: 'type',
      width: 96,
      render: (type: TextAssetType) => <Tag color={TYPE_TAG_COLOR[type]}>{t(`types.${type}`)}</Tag>,
    },
    {
      title: t('table.source'),
      dataIndex: 'source',
      key: 'source',
      width: 120,
      responsive: ['md'],
      render: (source: string) => t(`sources.${source}`, { defaultValue: source }),
    },
    {
      title: t('table.wordCount'),
      dataIndex: 'word_count',
      key: 'word_count',
      width: 90,
      render: (n: number) => t('table.wordCountUnit', { count: n }),
    },
    {
      title: t('table.creator'),
      key: 'creator',
      width: 140,
      responsive: ['lg'],
      render: (_: unknown, item: TextAssetItem) => {
        const label = creatorLabel(item)
        return (
          <Tooltip title={label}>
            <span className="tac-creator-cell" data-testid="tac-row-creator">
              {label}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: t('table.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (iso: string | null) => formatAbsoluteTime(iso),
    },
    {
      title: t('table.usageCount'),
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: 90,
      responsive: ['lg'],
      // Spec §49 — never fabricate a usage number; only render one when
      // the backend actually returned a non-null, reliably-sourced count.
      render: (n: number | null) => (n == null ? t('table.noUsage') : t('table.usageCountUnit', { count: n })),
    },
    {
      title: t('table.operations'),
      key: 'ops',
      width: 168,
      fixed: 'right' as const,
      render: (_: unknown, item) => (
        <div className="tac-row-ops" onClick={(e) => e.stopPropagation()}>
          <Tooltip title={t('actions.joinCanvas')}>
            <Button
              type="text"
              size="small"
              icon={<AppstoreAddOutlined />}
              onClick={() => void onJoinCanvas(item)}
              aria-label={t('actions.joinCanvas')}
              data-testid="tac-join-canvas"
            >
              {t('actions.joinCanvas')}
            </Button>
          </Tooltip>
          <Dropdown
            menu={{ items: textRowMenuItems(item) }}
            trigger={['click']}
            // Table scroll / fixed ops column must not clip the ⋮ menu
            // (especially the bottom danger 「删除」 item + divider).
            getPopupContainer={() => document.body}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="tac-icon-btn"
              icon={<MoreOutlined />}
              aria-label={tImageTeam('team.menu.more')}
              title={tImageTeam('team.menu.more')}
              data-testid="tac-row-more"
            />
          </Dropdown>
        </div>
      ),
    },
  ]

  if (!workspaceId) return null

  const hasAnyFilterActive = q.trim() !== '' || sourceFilter !== 'all'

  return (
    <div className="tac-page" data-testid="text-asset-center">
      <header className="tac-header">
        <div className="tac-heading">
          <h1 className="tac-title">{t('title')}</h1>
          <p className="tac-subtitle">{t('subtitle')}</p>
        </div>
        <div className="tac-actions">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
            data-testid="tac-new-text-btn"
          >
            {t('newTextButton')}
          </Button>
        </div>
      </header>

      {teamEnabled ? (
        <div className="iac-view-tabs" role="tablist" aria-label={tImageTeam('team.viewsAria')} data-testid="tac-view-tabs">
          {(['mine', 'shared', 'all'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={libraryView === v}
              className={`iac-view-tab${libraryView === v ? ' is-active' : ''}`}
              onClick={() => onChangeLibraryView(v)}
              data-testid={`tac-view-tab-${v}`}
            >
              {tImageTeam(`team.views.${v}`)}
            </button>
          ))}
        </div>
      ) : null}

      <Tabs
        activeKey={typeTab}
        onChange={(key) => {
          setTypeTab(key as TextAssetType | 'all')
          setPage(1)
        }}
        items={TYPE_TABS.map((tab) => ({
          key: tab,
          label: `${t(`types.${tab}`)} (${summary[tab.toLowerCase()] ?? 0})`,
        }))}
        data-testid="tac-type-tabs"
      />

      <section className="tac-filters">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t('filters.searchPlaceholder')}
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setPage(1)
          }}
          className="tac-search"
          data-testid="tac-search"
        />
        <Select
          value={sourceFilter}
          onChange={(v) => {
            setSourceFilter(v)
            setPage(1)
          }}
          options={sourceOptions}
          className="tac-filter-select"
          data-testid="tac-source-filter"
        />
        {teamEnabled && libraryView !== 'mine' ? (
          <Select
            value={creatorFilter}
            onChange={(v) => {
              setCreatorFilter(v)
              setPage(1)
            }}
            aria-label={tImageTeam('team.creator.filterAria')}
            className="tac-filter-select"
            data-testid="tac-creator-filter"
            options={buildCreatorSelectOptions(
              tImageTeam('team.creator.label'),
              tImageTeam('team.creator.all'),
              tImageTeam('team.creator.me'),
              team?.creator_options,
              team?.user_id,
            )}
          />
        ) : null}
        <Select
          value={sort}
          onChange={(v) => {
            setSort(v)
            setPage(1)
          }}
          options={[
            { value: 'updated_desc', label: t('filters.sortRecentUpdate') },
            { value: 'created_desc', label: t('filters.sortRecentCreate') },
            { value: 'name_asc', label: t('filters.sortName') },
            { value: 'word_count_desc', label: t('filters.sortWordCount') },
            { value: 'usage_desc', label: t('filters.sortUsage') },
          ]}
          className="tac-filter-select"
          data-testid="tac-sort"
        />
        <Tooltip title={t('refreshAria')}>
          <Button icon={<ReloadOutlined />} onClick={() => void load()} aria-label={t('refreshAria')} />
        </Tooltip>
      </section>

      {loadError && !loading ? (
        <div className="tac-state-block">
          <Alert type="error" showIcon message={t('error.loadFailed')} description={loadError} />
          <Button style={{ marginTop: 12 }} onClick={() => void load()}>
            {t('error.retry')}
          </Button>
        </div>
      ) : (
        <Table<TextAssetItem>
          rowKey="id"
          className="tac-table"
          loading={loading}
          columns={columns}
          dataSource={items}
          scroll={{ x: 960 }}
          data-testid="tac-table"
          locale={{
            emptyText: (
              <Empty description={hasAnyFilterActive ? t('empty.noMatch') : t('empty.noAssets')}>
                {!hasAnyFilterActive && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
                    {t('newTextButton')}
                  </Button>
                )}
              </Empty>
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
            showTotal: (count) => t('pagination.total', { count }),
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
          onRow={(item) => ({ onClick: () => void openDrawer(item.id), className: 'tac-row-clickable' })}
        />
      )}

      <Modal
        title={t('create.title')}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => void onCreate()}
        okText={t('create.submit')}
        cancelText={t('actions.cancel')}
        confirmLoading={creating}
        destroyOnHidden
      >
        <div className="tac-create-form">
          <label className="tac-form-label">{t('create.titleLabel')}</label>
          <Input
            value={createTitle}
            onChange={(e) => setCreateTitle(e.target.value)}
            placeholder={t('create.titlePlaceholder')}
            maxLength={300}
          />
          <label className="tac-form-label">{t('create.bodyLabel')}</label>
          <Input.TextArea
            value={createBody}
            onChange={(e) => setCreateBody(e.target.value)}
            placeholder={t('create.bodyPlaceholder')}
            autoSize={{ minRows: 6, maxRows: 14 }}
            maxLength={200000}
          />
        </div>
      </Modal>

      <Drawer
        title={
          drawerDetail ? (
            <div className="tac-drawer-title">
              <Tag color={TYPE_TAG_COLOR[drawerDetail.type]}>{t(`types.${drawerDetail.type}`)}</Tag>
              <span>{drawerDetail.title}</span>
            </div>
          ) : (
            t('actions.view')
          )
        }
        placement="right"
        width={480}
        open={drawerId != null}
        onClose={closeDrawer}
        data-testid="tac-detail-drawer"
        extra={
          drawerDetail ? (
            <div className="tac-drawer-extra">
              <Tooltip title={t('actions.copy')}>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => void onCopy(buildReadableText(drawerDetail, t))}
                  aria-label={t('actions.copy')}
                />
              </Tooltip>
              {canManageText(drawerDetail, team) && !editing && (
                <Tooltip title={t('actions.edit')}>
                  <Button size="small" icon={<EditOutlined />} onClick={startEdit} aria-label={t('actions.edit')} />
                </Tooltip>
              )}
              {canManageText(drawerDetail, team) && (
                <VistoeraActionConfirm
                  title={t('delete.confirmTitle', { name: drawerDetail.title || '—' })}
                  description={t('delete.confirmDescription', { name: drawerDetail.title || '—' })}
                  confirmLabel={tActions('delete')}
                  onConfirm={() => onDelete(drawerDetail.id)}
                >
                  <Tooltip title={t('actions.delete')}>
                    <Button size="small" danger icon={<DeleteOutlined />} aria-label={t('actions.delete')} />
                  </Tooltip>
                </VistoeraActionConfirm>
              )}
            </div>
          ) : null
        }
      >
        {drawerLoading ? (
          <div className="tac-drawer-loading">
            <Empty description={false} />
          </div>
        ) : drawerError ? (
          <Alert type="error" showIcon message={t('error.loadFailed')} description={drawerError} />
        ) : drawerDetail ? (
          <TextAssetDetailBody
            detail={drawerDetail}
            editing={editing}
            editTitle={editTitle}
            editBody={editBody}
            saving={saving}
            onEditTitleChange={setEditTitle}
            onEditBodyChange={setEditBody}
            onSave={() => void onSaveEdit()}
            onCancelEdit={() => setEditing(false)}
            onOpenSource={() => openSource(drawerDetail)}
          />
        ) : null}
      </Drawer>
    </div>
  )
}

function TextAssetDetailBody({
  detail,
  editing,
  editTitle,
  editBody,
  saving,
  onEditTitleChange,
  onEditBodyChange,
  onSave,
  onCancelEdit,
  onOpenSource,
}: {
  detail: TextAssetDetail
  editing: boolean
  editTitle: string
  editBody: string
  saving: boolean
  onEditTitleChange: (v: string) => void
  onEditBodyChange: (v: string) => void
  onSave: () => void
  onCancelEdit: () => void
  onOpenSource: () => void
}) {
  const { t } = useTranslation('textAssets')
  const [tab, setTab] = useState('content')

  const d = (detail.detail || {}) as Record<string, unknown>
  const canOpenSource =
    detail.canvas_available && (detail.type === 'TRANSCRIPT' ? true : Boolean(detail.project_id))

  const renderContent = () => {
    if (editing && detail.type === 'TEXT') {
      return (
        <div className="tac-edit-form">
          <label className="tac-form-label">{t('create.titleLabel')}</label>
          <Input value={editTitle} onChange={(e) => onEditTitleChange(e.target.value)} maxLength={300} />
          <label className="tac-form-label">{t('create.bodyLabel')}</label>
          <Input.TextArea
            value={editBody}
            onChange={(e) => onEditBodyChange(e.target.value)}
            autoSize={{ minRows: 10, maxRows: 24 }}
            maxLength={200000}
          />
          <div className="tac-edit-actions">
            <Button onClick={onCancelEdit}>{t('actions.cancel')}</Button>
            <Button type="primary" loading={saving} onClick={onSave}>
              {t('actions.save')}
            </Button>
          </div>
        </div>
      )
    }

    if (detail.type === 'TEXT' || detail.type === 'GENERATED_TEXT') {
      const text = typeof d.full_text === 'string' ? d.full_text : detail.preview_text
      return <div className="tac-body-text">{text || t('detail.content.empty')}</div>
    }

    if (detail.type === 'CREATIVE') {
      const result = (d.result || {}) as Record<string, unknown>
      const isDirector = isVideoDirectorProposal(result)
      const fields: Array<[string, string]> = isDirector
        ? [
            ['one_line_concept', t('detail.content.creativeOneLineConcept')],
            ['creative_direction', t('detail.content.creativeDirection')],
            ['narrative_structure', t('detail.content.creativeNarrativeStructure')],
            ['opening_hook', t('detail.content.creativeOpeningHook')],
            ['ending_image', t('detail.content.creativeEndingImage')],
            ['pacing', t('detail.content.creativePacing')],
            ['visual_style', t('detail.content.creativeVisualStyle')],
            ['camera_language', t('detail.content.creativeCameraLanguage')],
            ['subject_motion', t('detail.content.creativeSubjectMotion')],
            ['environment_motion', t('detail.content.creativeEnvironmentMotion')],
            ['audio_direction', t('detail.content.creativeAudioDirection')],
            ['why_it_fits', t('detail.content.creativeWhyItFits')],
            ['risks_or_notes', t('detail.content.creativeRisksOrNotes')],
          ]
        : [
            ['premise', t('detail.content.creativePremise')],
            ['hook', t('detail.content.creativeHook')],
            ['storyDirection', t('detail.content.creativeStoryDirection')],
            ['rationale', t('detail.content.creativeRationale')],
          ]
      const sections = fields.filter(([key]) => typeof result[key] === 'string' && (result[key] as string).trim())
      const shots = isDirector && Array.isArray(result.shots) ? (result.shots as VideoDirectorProposalShot[]) : []
      if (sections.length === 0 && shots.length === 0) return <div className="tac-body-text">{t('detail.content.empty')}</div>
      return (
        <div className="tac-structured">
          {sections.map(([key, label]) => (
            <div className="tac-structured-block" key={key}>
              <h4>{label}</h4>
              <p>{result[key] as string}</p>
            </div>
          ))}
          {shots.map((shot, idx) => (
            <div className="tac-structured-block" key={`shot-${idx}`}>
              <h4>{t('detail.content.storyboardShot', { index: shot.shot_index ?? idx + 1 })}</h4>
              {typeof shot.visual_description === 'string' && shot.visual_description.trim() ? <p>{shot.visual_description}</p> : null}
              {typeof shot.subject_action === 'string' && shot.subject_action.trim() ? (
                <p className="tac-muted">{shot.subject_action}</p>
              ) : null}
            </div>
          ))}
        </div>
      )
    }

    if (detail.type === 'SCRIPT') {
      const result = (d.result || {}) as Record<string, unknown>
      const scenes = Array.isArray(result.scenes) ? (result.scenes as ScriptScene[]) : []
      const logline = typeof result.logline === 'string' ? result.logline.trim() : ''
      const synopsis = typeof result.synopsis === 'string' ? result.synopsis.trim() : ''
      if (scenes.length === 0 && !logline && !synopsis) return <div className="tac-body-text">{t('detail.content.empty')}</div>
      return (
        <div className="tac-structured">
          {logline || synopsis ? (
            <div className="tac-structured-block">
              {logline ? (
                <>
                  <h4>{t('detail.content.scriptLogline')}</h4>
                  <p>{logline}</p>
                </>
              ) : null}
              {synopsis ? (
                <>
                  <h4>{t('detail.content.scriptSynopsis')}</h4>
                  <p>{synopsis}</p>
                </>
              ) : null}
            </div>
          ) : null}
          {scenes.map((scene, idx) => (
            <div className="tac-structured-block" key={idx}>
              <h4>{t('detail.content.scriptSceneFallback', { index: scene.scene_number ?? idx + 1 })}</h4>
              {typeof scene.setting === 'string' && scene.setting.trim() ? <p>{scene.setting}</p> : null}
              {typeof scene.action === 'string' && scene.action.trim() ? <p>{scene.action}</p> : null}
              {typeof scene.voiceover === 'string' && scene.voiceover.trim() ? (
                <p className="tac-muted">{scene.voiceover}</p>
              ) : null}
              {Array.isArray(scene.dialogue) && scene.dialogue.length > 0 ? (
                <div className="tac-script-dialogue">
                  {scene.dialogue.map((line, li) =>
                    line && typeof line.line === 'string' && line.line.trim() ? (
                      <p key={li}>
                        {line.character ? <strong>{line.character}：</strong> : null}
                        {line.line}
                      </p>
                    ) : null,
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )
    }

    if (detail.type === 'STORYBOARD') {
      const result = (d.result || {}) as Record<string, unknown>

      if (isVideoDirectorStoryboard(result)) {
        const shots = Array.isArray(result.shots) ? (result.shots as VideoDirectorStoryboardShot[]) : []
        const creativeSummary = typeof result.creative_summary === 'string' ? result.creative_summary.trim() : ''
        const narrativeScript = typeof result.narrative_script === 'string' ? result.narrative_script.trim() : ''
        if (shots.length === 0 && !creativeSummary && !narrativeScript) {
          return <div className="tac-body-text">{t('detail.content.empty')}</div>
        }
        return (
          <div className="tac-structured" data-testid="tac-storyboard-shots">
            <p className="tac-muted">{t('detail.content.storyboardShotCount', { count: shots.length })}</p>
            {creativeSummary ? (
              <div className="tac-structured-block">
                <h4>{t('detail.content.storyboardCreativeSummary')}</h4>
                <p>{creativeSummary}</p>
              </div>
            ) : null}
            {narrativeScript ? (
              <div className="tac-structured-block">
                <h4>{t('detail.content.storyboardNarrativeScript')}</h4>
                <p>{narrativeScript}</p>
              </div>
            ) : null}
            {shots.map((shot, idx) => (
              <div className="tac-structured-block" key={idx}>
                <h4>{t('detail.content.storyboardShot', { index: shot.shot_number ?? idx + 1 })}</h4>
                <dl className="tac-shot-fields">
                  {typeof shot.purpose === 'string' && shot.purpose.trim() ? (
                    <div className="tac-shot-field">
                      <dt>{t('detail.content.shotFieldPurpose')}</dt>
                      <dd>{shot.purpose}</dd>
                    </div>
                  ) : null}
                  {typeof shot.subject === 'string' && shot.subject.trim() ? (
                    <div className="tac-shot-field">
                      <dt>{t('detail.content.shotFieldSubject')}</dt>
                      <dd>{shot.subject}</dd>
                    </div>
                  ) : null}
                  {typeof shot.environment === 'string' && shot.environment.trim() ? (
                    <div className="tac-shot-field">
                      <dt>{t('detail.content.shotFieldEnvironment')}</dt>
                      <dd>{shot.environment}</dd>
                    </div>
                  ) : null}
                  {typeof shot.duration_seconds === 'number' ? (
                    <div className="tac-shot-field">
                      <dt>{t('detail.content.shotFieldDuration')}</dt>
                      <dd>{shot.duration_seconds}s</dd>
                    </div>
                  ) : null}
                </dl>
                {typeof shot.visual_description === 'string' && shot.visual_description.trim() ? (
                  <p>{shot.visual_description}</p>
                ) : null}
                {typeof shot.subject_action === 'string' && shot.subject_action.trim() ? (
                  <p className="tac-muted">{shot.subject_action}</p>
                ) : null}
              </div>
            ))}
          </div>
        )
      }

      const shots = Array.isArray(result.shots) ? (result.shots as StoryboardShot[]) : []
      const synopsis = typeof result.synopsis === 'string' ? result.synopsis.trim() : ''
      if (shots.length === 0 && !synopsis) return <div className="tac-body-text">{t('detail.content.empty')}</div>
      return (
        <div className="tac-structured" data-testid="tac-storyboard-shots">
          <p className="tac-muted">{t('detail.content.storyboardShotCount', { count: shots.length })}</p>
          {synopsis ? (
            <div className="tac-structured-block">
              <h4>{t('detail.content.storyboardSynopsis')}</h4>
              <p>{synopsis}</p>
            </div>
          ) : null}
          {shots.map((shot, idx) => {
            const sceneLabel = shotSceneLabel(shot.scene)
            return (
              <div className="tac-structured-block" key={idx}>
                <h4>{t('detail.content.storyboardShot', { index: (shot.shot_index ?? idx) + 1 })}</h4>
                <dl className="tac-shot-fields">
                  {sceneLabel ? (
                    <div className="tac-shot-field">
                      <dt>{t('detail.content.shotFieldScene')}</dt>
                      <dd>{sceneLabel}</dd>
                    </div>
                  ) : null}
                  {typeof shot.title === 'string' && shot.title.trim() ? (
                    <div className="tac-shot-field">
                      <dt>{t('table.name')}</dt>
                      <dd>{shot.title}</dd>
                    </div>
                  ) : null}
                  {typeof shot.description === 'string' && shot.description.trim() ? (
                    <div className="tac-shot-field">
                      <dt>{t('detail.content.shotFieldDescription')}</dt>
                      <dd>{shot.description}</dd>
                    </div>
                  ) : null}
                  {typeof shot.camera_description === 'string' && shot.camera_description.trim() ? (
                    <div className="tac-shot-field">
                      <dt>{t('detail.content.shotFieldCamera')}</dt>
                      <dd>{shot.camera_description}</dd>
                    </div>
                  ) : null}
                  {typeof shot.duration_sec === 'number' ? (
                    <div className="tac-shot-field">
                      <dt>{t('detail.content.shotFieldDuration')}</dt>
                      <dd>{shot.duration_sec}s</dd>
                    </div>
                  ) : null}
                </dl>
                {Array.isArray(shot.dialogue) && shot.dialogue.length > 0 ? (
                  <div className="tac-script-dialogue">
                    {shot.dialogue.map((line, li) =>
                      line && typeof line.text === 'string' && line.text.trim() ? <p key={li}>{line.text}</p> : null,
                    )}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      )
    }

    if (detail.type === 'TRANSCRIPT') {
      const segments = Array.isArray(d.segments) ? (d.segments as TranscriptSegmentRow[]) : []
      if (segments.length === 0) return <div className="tac-body-text">{t('detail.content.empty')}</div>
      return (
        <div className="tac-transcript-list">
          {segments.map((seg) => (
            <div className="tac-transcript-row" key={seg.segment_index}>
              <span className="tac-transcript-time">{msToClock(seg.start_ms)}</span>
              <span className="tac-transcript-speaker">{seg.speaker || t('detail.content.transcriptSpeakerFallback')}</span>
              <span className="tac-transcript-text">{seg.text}</span>
            </div>
          ))}
        </div>
      )
    }

    return <div className="tac-body-text">{t('detail.content.empty')}</div>
  }

  return (
    <Tabs
      activeKey={tab}
      onChange={setTab}
      items={[
        { key: 'content', label: t('detail.tabs.content'), children: renderContent() },
        {
          key: 'info',
          label: t('detail.tabs.info'),
          children: (
            <div className="tac-info-list">
              <div className="tac-info-row">
                <span>{t('detail.info.type')}</span>
                <span>{t(`types.${detail.type}`)}</span>
              </div>
              <div className="tac-info-row">
                <span>{t('detail.info.source')}</span>
                <span>{t(`sources.${detail.source}`, { defaultValue: detail.source })}</span>
              </div>
              {detail.role && (
                <div className="tac-info-row">
                  <span>{t('detail.info.role')}</span>
                  <span>{detail.role}</span>
                </div>
              )}
              <div className="tac-info-row">
                <span>{t('detail.info.wordCount')}</span>
                <span>{detail.word_count}</span>
              </div>
              <div className="tac-info-row">
                <span>{t('detail.info.createdAt')}</span>
                <span>{formatAbsoluteTime(detail.created_at)}</span>
              </div>
              <div className="tac-info-row">
                <span>{t('detail.info.updatedAt')}</span>
                <span>{formatAbsoluteTime(detail.updated_at)}</span>
              </div>
            </div>
          ),
        },
        {
          key: 'relations',
          label: t('detail.tabs.relations'),
          children: (
            <div className="tac-info-list">
              <div className="tac-info-row">
                <span>{t('detail.relations.source')}</span>
                <span>{t(`sources.${detail.source}`, { defaultValue: detail.source })}</span>
              </div>
              <div className="tac-info-row">
                <span>{t('detail.relations.project')}</span>
                <span>{detail.project_title || t('detail.relations.noProject')}</span>
              </div>
              <div className="tac-info-row">
                <span>{t('detail.relations.canvasNode')}</span>
                <span>{canonicalNodeLabel(detail.canonical_source_type, t)}</span>
              </div>
              {typeof d.skill_key === 'string' && (
                <div className="tac-info-row">
                  <span>{t('detail.relations.run')}</span>
                  <span>{d.skill_key}</span>
                </div>
              )}
              <div className="tac-info-row">
                <span>{t('detail.relations.derivedFrom')}</span>
                <span>{t('detail.relations.notAvailable')}</span>
              </div>
              <div className="tac-info-row-action">
                {canOpenSource ? (
                  <Button size="small" icon={<ExportOutlined />} onClick={onOpenSource}>
                    {t('actions.openSource')}
                  </Button>
                ) : (
                  <span className="tac-muted">{t('detail.relations.sourceUnavailable')}</span>
                )}
              </div>
            </div>
          ),
        },
        {
          key: 'usage',
          label: t('detail.tabs.usage'),
          children: (
            <div className="tac-info-list">
              <div className="tac-info-row">
                <span>{t('detail.usage.count')}</span>
                <span>{detail.usage_count == null ? t('detail.usage.unavailable') : detail.usage_count}</span>
              </div>
            </div>
          ),
        },
      ]}
    />
  )
}
